// Route registry — app khai báo routes 1 lần, plugin sinh typed nav tools tự động.
//
// Tránh tool "navigate_to(path)" generic dễ bị AI hallucinate path. Thay bằng
// nhiều typed tool (vd `view_item_detail`, `view_admin_items_list`) với param
// schema chặt → AI không thể đoán path sai.

/// Một entry trong route registry.
export interface RouteEntry {
  /// Tool name AI thấy. Convention: bắt đầu bằng action verb (`view_*`, `open_*`).
  /// Phải unique trong registry. AI dùng tên này để gọi tool.
  name: string;
  /// URL pattern app, có thể chứa `:param`. Vd: `/items/:id`, `/admin/items`.
  path: string;
  /// Description cho AI (system context). Giải thích khi nào dùng tool này.
  description: string;
  /// Param definitions (cho :param trong path + optional query params).
  /// Vd: `{ id: { type: "string", required: true, description: "Real item UUID" } }`
  params?: Record<string, RouteParamSpec>;
}

export interface RouteParamSpec {
  type: "string" | "number";
  description?: string;
  required?: boolean;
}

/// Shape của 1 tool sinh ra. Trùng schema với SDK Tool — app có thể spread vào
/// Tool[] trực tiếp (plus thêm risk: "low" vì nav không destructive).
export interface NavToolDef {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<unknown>;
}

/// Tạo 1 typed nav tool từ entry + navigate fn.
/// Tool's `execute` thay :param bằng args + gọi navigate.
export function createTypedNavTool(
  entry: RouteEntry,
  navigate: (path: string) => void,
): NavToolDef {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];

  if (entry.params) {
    for (const [k, v] of Object.entries(entry.params)) {
      properties[k] = {
        type: v.type,
        ...(v.description ? { description: v.description } : {}),
      };
      if (v.required) required.push(k);
    }
  }

  const parameters: Record<string, unknown> = {
    type: "object",
    properties,
  };
  if (required.length > 0) parameters.required = required;

  return {
    name: entry.name,
    description: entry.description,
    parameters,
    execute: async (args: Record<string, unknown>) => {
      let path = entry.path;
      // Replace :param placeholders bằng args value
      if (entry.params) {
        for (const k of Object.keys(entry.params)) {
          if (path.includes(`:${k}`)) {
            const v = args[k];
            if (v === undefined || v === null || v === "") {
              throw new Error(`Missing required param '${k}' for route '${entry.path}'`);
            }
            path = path.replace(`:${k}`, encodeURIComponent(String(v)));
          }
        }
      }
      navigate(path);
      return { navigated: true, path };
    },
  };
}

/// Convenience: tạo array typed nav tools cho cả registry.
export function createTypedNavTools(
  registry: RouteEntry[],
  navigate: (path: string) => void,
): NavToolDef[] {
  return registry.map((entry) => createTypedNavTool(entry, navigate));
}

/// Validate registry: check unique names, check :param trong path có declare ở params.
/// Throw nếu invalid — gọi lúc app init để fail fast.
export function validateRouteRegistry(registry: RouteEntry[]): void {
  const seen = new Set<string>();
  for (const entry of registry) {
    if (seen.has(entry.name)) {
      throw new Error(`Duplicate route name: '${entry.name}'`);
    }
    seen.add(entry.name);

    // Extract :params từ path
    const pathParams = (entry.path.match(/:([a-zA-Z_][a-zA-Z0-9_]*)/g) ?? []).map((s) =>
      s.slice(1),
    );
    for (const p of pathParams) {
      if (!entry.params || !entry.params[p]) {
        throw new Error(
          `Route '${entry.name}' path '${entry.path}' contains :${p} but no params.${p} declared`,
        );
      }
    }
  }
}
